import { useLang } from "../lib/i18n";
import { MapPin } from "lucide-react";
import useReveal from "../hooks/useReveal";

const MONTERREY_EMBED =
    "https://www.google.com/maps?q=Monterrey%2C%20Nuevo%20Le%C3%B3n%2C%20M%C3%A9xico&z=11&output=embed";
const WOODLANDS_EMBED =
    "https://www.google.com/maps?q=The%20Woodlands%2C%20Texas%2077386%2C%20USA&z=12&output=embed";

const MONTERREY_OPEN =
    "https://www.google.com/maps?q=Monterrey%2C%20Nuevo%20Le%C3%B3n%2C%20M%C3%A9xico";
const WOODLANDS_OPEN =
    "https://www.google.com/maps?q=The%20Woodlands%2C%20Texas%2077386%2C%20USA";

export const LocationMap = () => {
    const { t } = useLang();
    const { ref, visible } = useReveal();

    return (
        <section
            id="location"
            ref={ref}
            className={`relative py-24 md:py-32 border-t border-navy/10 reveal ${
                visible ? "visible" : ""
            }`}
            data-testid="location-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-14">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-navy/60">
                            <span className="tick" />
                            {t.location.kicker}
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
                            data-testid="location-heading"
                        >
                            {t.location.heading}
                        </h2>
                        <p className="mt-6 text-navy/70 max-w-xl text-lg">
                            {t.location.copy}
                        </p>
                    </div>
                </div>

                {/* Two map cards side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* Monterrey */}
                    <div
                        className="relative border border-navy/15 bg-cream overflow-hidden group"
                        data-testid="map-card-monterrey"
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/10">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-warm">
                                <MapPin className="w-4 h-4 text-navy" strokeWidth={2} />
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                    {t.location.primaryLabel} · 🇲🇽
                                </div>
                                <div
                                    className="font-display text-lg text-navy"
                                    data-testid="location-city"
                                >
                                    {t.location.city}
                                </div>
                            </div>
                        </div>
                        <div className="aspect-[4/3] w-full">
                            <iframe
                                title="Monterrey, Nuevo León, México"
                                src={MONTERREY_EMBED}
                                className="w-full h-full border-0 grayscale-[15%] group-hover:grayscale-0 transition-all duration-700"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                                data-testid="google-map-iframe"
                            />
                        </div>
                        <a
                            href={MONTERREY_OPEN}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-navy text-cream px-4 py-2 text-xs tracking-wide hover:bg-warm-600 transition-colors"
                            data-testid="open-map-mty"
                        >
                            Google Maps →
                        </a>
                    </div>

                    {/* The Woodlands, TX */}
                    <div
                        className="relative border border-navy/15 bg-cream overflow-hidden group"
                        data-testid="map-card-woodlands"
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/10">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accentblue">
                                <MapPin className="w-4 h-4 text-cream" strokeWidth={2} />
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                    {t.location.secondaryLabel} · 🇺🇸
                                </div>
                                <div
                                    className="font-display text-lg text-navy"
                                    data-testid="location-city-2"
                                >
                                    {t.location.city2}
                                </div>
                            </div>
                        </div>
                        <div className="aspect-[4/3] w-full">
                            <iframe
                                title="The Woodlands, Texas 77386, USA"
                                src={WOODLANDS_EMBED}
                                className="w-full h-full border-0 grayscale-[15%] group-hover:grayscale-0 transition-all duration-700"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                                data-testid="google-map-iframe-2"
                            />
                        </div>
                        <a
                            href={WOODLANDS_OPEN}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-navy text-cream px-4 py-2 text-xs tracking-wide hover:bg-accentblue transition-colors"
                            data-testid="open-map-tx"
                        >
                            Google Maps →
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LocationMap;
